import { useViewPort } from "@website/hooks/viewport";
import { useTable } from "@website/ui-old/datepicker/components/table/table.hook";
import styles from "@website/ui-old/datepicker/components/table/table.module.scss";
import { TablePropertiesType } from "@website/ui-old/datepicker/components/table/table.types";
import { isSSR } from "@website/utils/is-ssr";
import { numberConvert } from "@website/utils/number-convert";
import { FC, memo, useEffect } from "react";

const MONTH_NAMES: Record<string, Array<string>> = {
  persian: [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند"
  ],
  gregorian: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]
};

const WEEK_ABBR: Record<string, Array<string>> = {
  persian: ["ش", "ی", "د", "س", "چ", "پ", "ج"],
  gregorian: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
};

const Table: FC<TablePropertiesType> = (properties) => {
  const { calendarLocale, year, month, prices } = properties;
  const { days } = useTable(properties);
  const { isMobile } = useViewPort();

  useEffect(() => {
    if (isMobile && !isSSR()) {
      const element =
        document?.querySelector("#datepicker_selected") ||
        document?.querySelector("#datepicker_start") ||
        document?.querySelector("#datepicker_multi-selected");
      if (element)
        element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isMobile]);

  return (
    <div className={`${styles["container"]} ${styles["rtl" || "ltr"]}`}>
      <div className={styles["week-names-container-desktop"]}>
        {[1, 2, 3, 4, 5, 6, 7].map((week, weekIndex) => (
          <div className={styles["week-name"]} key={weekIndex++}>
            {WEEK_ABBR[calendarLocale]?.[week - 1] ||
              WEEK_ABBR.gregorian[week - 1]}
          </div>
        ))}
      </div>
      <div className={styles["month-name"]}>
        {(MONTH_NAMES[calendarLocale] || MONTH_NAMES.gregorian)[month - 1]}{" "}
        {numberConvert(year, {
          numberOnly: true,
          locale: "fa"
        })}
        {(calendarLocale === "persian" && String("fa") !== "fa") ||
        (calendarLocale === "gregorian" && String("fa") !== "en") ? (
          <span className={styles["month-name-native"]}>
            ({(MONTH_NAMES[calendarLocale] || MONTH_NAMES.gregorian)[month - 1]}
            )
          </span>
        ) : null}
      </div>
      {[0, 1, 2, 3, 4, 5].map((row, rowIndex) => {
        if (
          days[rowIndex * 7]?.value !== -1 || // first cell of row
          days[rowIndex * 7 + 6]?.value !== -1 // last cell of row
        ) {
          return (
            <div className={styles["row"]} key={rowIndex++}>
              {[0, 1, 2, 3, 4, 5, 6].map((cell, cellIndex) => {
                const index = rowIndex * 7 + cellIndex;
                const cellText =
                  days[index]?.value === -1 ? "" : days[index]?.value;
                const cellContent = days[index]?.content;
                const state = days[index]?.state;
                const isToday = days[index]?.isToday;
                const cellType = days[index]?.type;

                return (
                  <div
                    className={`${styles["cell"]} ${
                      isToday ? styles["today"] : ""
                    } ${state ? styles[state] : ""}`}
                    id={`datepicker_${state}`}
                    onClick={days[index]?.onClick}
                  >
                    <div
                      className={`${styles["cell-inside"]} ${
                        cellType === "lowest"
                          ? `bg-transparent-success rounded`
                          : ""
                      }`}
                    >
                      {numberConvert(cellText, {
                        numberOnly: true,
                        locale: "fa"
                      })}
                      {cellContent ? (
                        <span className={styles["cell-inside-data"]}>
                          {Number.isNaN(Number(cellContent))
                            ? cellContent
                            : prices
                              ? Number(cellContent) > 0 &&
                                numberConvert(
                                  (Number(cellContent) / 10_000_000).toFixed(1),
                                  {
                                    locale: "fa"
                                  }
                                )
                              : numberConvert(
                                  Number(cellContent).toLocaleString("en-US"),
                                  {
                                    numberOnly: false,
                                    locale: "fa"
                                  }
                                )}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const TableMemoized = memo(Table);
/**
 * @deprecated please use @website/ui instead
 */
export { TableMemoized /**
 * @deprecated please use @website/ui instead
 */ as Table };
